import { Alert, CircularProgress } from "@mui/material";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { ThreeDPanelState, usePanelState } from "@/shared/state/visualization";
import type { EntityID } from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./EntityConfig.module.css";
import { ModelFilePicker, type PickedModel } from "./ModelFilePicker";
import { planModelRepick } from "./modelRepick";
import { useImportModelIntoEntity } from "./useImportModelIntoEntity";

interface ModelImportControlProps {
  /** The entity whose mesh will name the chosen file. */
  entityId: EntityID;
  panelId: string;
  /** Whether picking an unconverted file may start a conversion. */
  canIngest: boolean;
  /** Called once the model is on the entity, so the caller can dismiss this. */
  onImported: () => void;
  /** Called when the pick was abandoned or its error dismissed. */
  onCancelled: () => void;
}

/**
 * Picking a model file and pointing one entity at it.
 *
 * Split out of `MeshConfig` and mounted only while a model is being chosen,
 * because importing needs to fetch and parse a file: it depends on the domain
 * services and on org context. Rendering a sphere's radius should not. Keeping
 * that dependency inside this component means the mesh form stays a plain set of
 * controls that any tree can render.
 */
export function ModelImportControl({
  entityId,
  panelId,
  canIngest,
  onImported,
  onCancelled,
}: ModelImportControlProps) {
  const panelState = usePanelState(panelId) as ThreeDPanelState;
  const { importing, error, importModel, clearError } =
    useImportModelIntoEntity(panelId);
  const [picking, setPicking] = React.useState(true);
  /**
   * Why a pick could not be turned into a model, when that is what went wrong.
   *
   * Kept apart from the import hook's own error because it happens before any
   * import starts, but shown through the same alert: from the user's side both
   * are "the model you chose did not arrive".
   */
  const [pickError, setPickError] = React.useState<string | undefined>();
  /** Set once a file is chosen, so a trailing close is not read as a cancel. */
  const submittedRef = React.useRef(false);

  // The import checks the chosen entity against this, so an entity deleted
  // while the picker was open is caught rather than given a mesh nothing draws.
  // The fallback covers a workspace saved before the registry existed.
  const registry = React.useMemo(
    () =>
      panelState.config?.entities ??
      Object.fromEntries(
        Object.keys(panelState.data.entities).map((id) => [id, {}]),
      ),
    [panelState.config?.entities, panelState.data.entities],
  );

  /** The model this entity draws today, if it draws one. */
  const currentModel = React.useMemo(() => {
    const mesh = panelState.data.entities[entityId]?.mesh;
    return mesh?.type === "model" ? mesh : undefined;
  }, [panelState.data.entities, entityId]);

  const onSubmit = React.useCallback(
    (picked: PickedModel) => {
      setPicking(false);
      submittedRef.current = true;

      // Re-picking is a real gesture now that the model field opens this, so
      // what a pick means depends on what the entity already draws — including
      // "nothing at all", when the same model is chosen again.
      const plan = planModelRepick(currentModel, picked);
      if (plan.action === "keep") {
        onImported();
        return;
      }

      // `source` is always here now — the picker offers only robot descriptions,
      // and every one resolves to an artifact. `importModel` still takes it
      // optionally, because the mesh schema keeps it optional for workspaces
      // saved when a plain glTF could be picked.
      void importModel(
        picked.model,
        entityId,
        registry,
        picked.source,
        plan.jointValues,
      ).then((imported) => {
        // On failure this stays mounted so the alert below can be read; it is
        // dismissing that which finally cancels.
        if (imported) {
          onImported();
        }
      });
    },
    [currentModel, entityId, importModel, onImported, registry],
  );

  const onPickFailed = React.useCallback((message: string) => {
    setPicking(false);
    submittedRef.current = true;
    setPickError(message);
  }, []);

  const onClose = React.useCallback(() => {
    setPicking(false);
    // The browser closes itself after a pick as well as after a dismissal, and
    // the two must not be confused: reporting a cancel once an import is already
    // under way would tell the caller to revert a choice that is about to
    // succeed.
    if (!submittedRef.current) {
      onCancelled();
    }
  }, [onCancelled]);

  return (
    <>
      {picking && (
        <ModelFilePicker
          entityId={entityId}
          canIngest={canIngest}
          onClose={onClose}
          onSubmit={onSubmit}
          onError={onPickFailed}
        />
      )}

      {importing && (
        <div className={styles.fieldRow}>
          <CircularProgress size={16} />
          <RobotoTypography variant="caption">
            Importing model…
          </RobotoTypography>
        </div>
      )}

      {(error ?? pickError) !== undefined && (
        <Alert
          severity="error"
          onClose={() => {
            clearError();
            setPickError(undefined);
            onCancelled();
          }}
        >
          {error ?? pickError}
        </Alert>
      )}
    </>
  );
}
